import couples from '@/assets/audience/couples.jpg';
import social from '@/assets/audience/social.jpg';
import business from '@/assets/audience/business.jpg';
import entrepreneurs from '@/assets/audience/entrepreneurs.jpg';
import influencers from '@/assets/audience/influencers.jpg';
import parents from '@/assets/audience/parents.jpg';

/**
 * Audience landing pages. Content rules: no invented events, venues, dates,
 * testimonials or availability. Availability is read from the database at render.
 */
export interface AudiencePage {
  slug: string;
  mode: string;
  label: string;
  image: string;
  imageAlt: string;
  title: string;
  description: string;
  eyebrow: string;
  h1: string;
  intro: string;
  forWho: string[];
  evening: string[];
  faqs: { q: string; a: string }[];
}

export const AUDIENCE_PAGES: AudiencePage[] = [
  {
    slug: 'couples-dinner-experiences', mode: 'couples', label: 'Couples', image: couples,
    imageAlt: 'Two couples sharing a candlelit dinner',
    title: 'Couples Dinner Experiences | Amorette',
    description: 'Curated dinners where couples meet other couples over an intimate, hand-planned evening. Join the Amorette waitlist for your city.',
    eyebrow: 'For couples', h1: 'Evenings for couples who want to meet other couples',
    intro: 'Amorette places couples at a small, curated table with other couples. It is a relaxed way to widen your social circle together without the effort of planning.',
    forWho: ['Couples new to a city who want to build a shared circle', 'Partners looking for a fresh night out together', 'Couples who prefer conversation over crowded events'],
    evening: ['You and your partner join together as one couple.', 'We seat you with a small group of other couples based on your shared interests.', 'The restaurant is revealed to confirmed guests shortly before the evening.', 'Afterwards you can privately choose which couples you would like to see again.'],
    faqs: [
      { q: 'Is this a singles or dating event?', a: 'No. Couples experiences are only for couples. Singles experiences are kept completely separate.' },
      { q: 'Do we both need an account?', a: 'Each partner verifies their identity, and you link your profiles as a couple.' },
      { q: 'Are meals included?', a: 'No. You order and pay for food and drinks directly with the restaurant.' },
    ],
  },
  {
    slug: 'social-dinner-clubs', mode: 'social', label: 'Social & Clubs', image: social,
    imageAlt: 'Friends laughing around a long dinner table',
    title: 'Social Dinners & Social Clubs | Amorette',
    description: 'Meet new friends at curated small-table dinners and social clubs. No dating, just good company. Join the Amorette waitlist.',
    eyebrow: 'Social connections', h1: 'Make new friends over a curated dinner',
    intro: 'Social experiences are for people who want genuine friendships and a regular social circle. These are not dating events, and they are never used as dating events in disguise.',
    forWho: ['People who recently moved and want to meet locals', 'Anyone ready to grow their circle of friends', 'Members who enjoy returning to a recurring social club'],
    evening: ['Tell us what you enjoy and the kind of company you are looking for.', 'We seat you at a small table of like-minded guests.', 'Social clubs can continue as a recurring group after the first evening.', 'Messaging only opens when both people choose to connect.'],
    faqs: [
      { q: 'Is this dating?', a: 'No. Social experiences are for friendship. If you are seeking romance, choose Singles instead.' },
      { q: 'What is a social club?', a: 'A small group of members who keep meeting for future evenings once a club forms in your city.' },
      { q: 'Can I come alone?', a: 'Yes. Most guests arrive on their own.' },
    ],
  },
  {
    slug: 'business-networking-dinners', mode: 'business', label: 'Business', image: business,
    imageAlt: 'Professionals talking over an intimate business dinner',
    title: 'Business Networking Dinners | Amorette',
    description: 'Small, curated business dinners for professionals who want real conversations instead of crowded networking events.',
    eyebrow: 'Business connections', h1: 'Business connections over a proper dinner',
    intro: 'A curated table of professionals, chosen for relevant but complementary backgrounds. Conversation comes easily, with no name tags or sales pitches.',
    forWho: ['Professionals who find traditional networking events unproductive', 'People changing industries or cities', 'Leaders who want a trusted local peer group'],
    evening: ['Share your field, goals and the kind of people you would like to meet.', 'We seat you with a small group of verified professionals.', 'The venue is revealed to confirmed guests before the evening.', 'Afterwards, connect privately with anyone who also wants to stay in touch.'],
    faqs: [
      { q: 'Is this a dating event?', a: 'No. Business experiences are strictly professional and kept separate from singles experiences.' },
      { q: 'Who attends?', a: 'Identity-verified members who chose business connection as their goal.' },
      { q: 'Can I pitch at the table?', a: 'The evenings are designed for conversation, not selling. Our community guidelines apply.' },
    ],
  },
  {
    slug: 'entrepreneur-dinners', mode: 'entrepreneurs', label: 'Entrepreneurs', image: entrepreneurs,
    imageAlt: 'Founders sharing ideas at a candlelit chef’s counter',
    title: 'Entrepreneur Dinners | Amorette',
    description: 'Curated dinners for founders and entrepreneurs to meet peers in their city. Join the Amorette waitlist.',
    eyebrow: 'Entrepreneur connections', h1: 'A table of founders who understand the work',
    intro: 'Building something can be isolating. Entrepreneur experiences seat you with other founders and operators for an honest, relaxed evening.',
    forWho: ['Founders at any stage', 'Independent business owners and creators of new ventures', 'Operators who want a trusted peer circle'],
    evening: ['Tell us what you are building and what you would find helpful.', 'We curate a small table of fellow entrepreneurs.', 'Share ideas, challenges and introductions over dinner.', 'Stay in touch through mutual, opt-in connections.'],
    faqs: [
      { q: 'Is this an investor event?', a: 'No. It is peer connection, not a pitch night.' },
      { q: 'Is this dating?', a: 'No. Entrepreneur experiences are professional and separate from singles experiences.' },
      { q: 'Do I need a funded company?', a: 'No. Any stage is welcome.' },
    ],
  },
  {
    slug: 'influencer-dinners', mode: 'influencers', label: 'Influencers', image: influencers,
    imageAlt: 'Creators at an elegant rooftop dinner at dusk',
    title: 'Dinners for Influencers & Creators | Amorette',
    description: 'Private, curated dinners where creators and influencers meet peers offline. Join the Amorette waitlist.',
    eyebrow: 'Influencers & creators', h1: 'Offline connection for creators',
    intro: 'A private evening with other creators, focused on real conversation rather than content. Venues are never advertised publicly.',
    forWho: ['Creators who want peers who understand the work', 'Influencers looking for collaborators offline', 'Anyone who values a private evening away from the camera'],
    evening: ['Share your niche and what you are hoping to find.', 'We seat you with a small table of verified creators.', 'Privacy comes first: guests are verified and the venue is shared only with confirmed guests.', 'Connect afterwards only when both people agree.'],
    faqs: [
      { q: 'Can I film the evening?', a: 'Please respect other guests’ privacy. Do not record anyone without their consent.' },
      { q: 'Is this a brand event?', a: 'No. There are no sponsors or brand activations. It is peer connection.' },
      { q: 'Is it dating?', a: 'No. Influencer experiences are social and separate from singles experiences.' },
    ],
  },
  {
    slug: 'parents-dinners', mode: 'parents', label: 'Parents', image: parents,
    imageAlt: 'Two adults enjoying a relaxed evening dinner',
    title: 'Dinners for Parents & Single Parents | Amorette',
    description: 'Curated evenings for parents, including single mothers and single fathers, to meet other parents or date. Join the Amorette waitlist.',
    eyebrow: 'Parents & single parents', h1: 'An evening out, planned for parents',
    intro: 'Parents rarely get time to plan a night out. Amorette handles the planning so you can meet other parents. That includes single mothers and fathers who want friendship, or romance if they choose it.',
    forWho: ['Parents who want to meet other parents socially', 'Single mothers and single fathers ready to meet new people', 'Anyone who wants a well-planned evening that is worth the babysitter'],
    evening: ['Tell us whether you are looking for friendship or romance. They are kept separate.', 'We seat you with a small group of parents with similar goals.', 'The restaurant is revealed to confirmed guests before the evening.', 'Messaging opens only when both people choose to connect.'],
    faqs: [
      { q: 'Are these singles dinners?', a: 'Only if you choose romance. Single parents seeking romance join singles-mode evenings. Social parent evenings are for friendship.' },
      { q: 'Are children welcome?', a: 'No. These are adult evenings at restaurants.' },
      { q: 'Is my family information shared?', a: 'No. You decide what to share on your profile.' },
    ],
  },
];

export const getAudiencePage = (slug: string) => AUDIENCE_PAGES.find((p) => p.slug === slug);
